import { Component, HostBinding, ViewChild, ViewEncapsulation, OnInit, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, AfterViewChecked } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { isNullOrUndefined } from '../../../../models/utils';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { PasswordAndUserLogoutPolicy, PasswordAndUserLogoutPolicyViewModel } from '../../../../models/password-user-logout-policy';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { ScrollablePopoverDirective } from '../../../../directives/scrollable-popover/scrollable-popover.directive';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { Permissions, ActionType, ObjectActionListType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-password-and-user-logout-policy',
    templateUrl: './password-and-user-logout-policy.component.html',
    styleUrls: ['./password-and-user-logout-policy.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PasswordAndUserLogoutPolicyComponent extends BaseModalComponent implements SettingsNavigationContentComponent, OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    settings: PasswordAndUserLogoutPolicy;
    viewModel: PasswordAndUserLogoutPolicyViewModel;
    passwordAndUserLogoutPolicyAutomationId = 'password-and-user-logout-policy-';
    passwordInputSize = 4;
    inputSize = 2;
    errorMessage = 'This field must not be blank. The current setting value has been entered.';
    errors = {
        passwordExpirationInDays: false,
        minimumLowercaseLetters: false,
        minimumNumbers: false,
        minimumPasswordLength: false,
        minimumSymbols: false,
        minimumUppercaseLetters: false,
        inactivityTimeoutInSeconds: false
    }
    defaultZero = 0;
    defaultTwenty = 20;
    defaultSix = 6;
    defaultNinety = 90;
    hasCreatePermissions: boolean;
    isTPAuthEnabled: boolean;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private modalService: BsModalService,
        private modalRef: BsModalRef, private changeDetector: ChangeDetectorRef) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.viewModel = new PasswordAndUserLogoutPolicyViewModel(new PasswordAndUserLogoutPolicy);
        this.isTPAuthEnabled = this.commonService.isTPAuthEnabledSystem;
        this.loadSettingsData();
    }

    loadSettingsData() {
        this.isBusyMessage = 'Loading Email Server & Notifications ...';
        this.isBusy = true;
        this.isDirty = false;
        this.restService.post(this.isTPAuthEnabled ? Endpoint.realmManager_getTPAuthPasswordPolicy : Endpoint.realmManager_getPasswordPolicy, { systemScope: true }).then(response => {
            if (response) {
                let formattedResponse;
                if (this.isTPAuthEnabled) {
                    formattedResponse = new PasswordAndUserLogoutPolicy({
                        enforceStrongPasswords: false,
                        minimumLowercaseLetters: 0,
                        minimumNumbers: 0,
                        minimumPasswordLength: response?.tpAuthPasswordPolicy?.requirements?.minLength ? response?.tpAuthPasswordPolicy.requirements.minLength : 0,
                        minimumSymbols: 0,
                        minimumUppercaseLetters: 0,
                        mustHaveCharacters: '',
                        passwordExpirationInDays: 90,
                        historyLength: response?.tpAuthPasswordPolicy?.requirements?.historyLength ? response?.tpAuthPasswordPolicy.requirements.historyLength : 0,
                    });
                } else {
                    formattedResponse = response;
                }
                this.populatePasswordPolicyDataFromResponse(formattedResponse);
            }
        }, error => {
            this.isBusy = false;
        });
    }

    populatePasswordPolicyDataFromResponse(response: Partial<PasswordAndUserLogoutPolicy>): void {
        this.settings = new PasswordAndUserLogoutPolicy(response);

        this.restService.post(Endpoint.realmManager_getInactivityTimeout, { systemScope: true }).then(response => {
            if (response) {
                this.settings.inactivityTimeoutInSeconds = response.inactivityTimeoutInSeconds / 60;
                this.viewModel = new PasswordAndUserLogoutPolicyViewModel(this.settings);
                this.isBusy = false;
                this.isDirty = false;
            }
        }, error => {
            this.isBusy = false;
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode): void {
        this.helpIndex = HelpIndex.sys_set_passwd_logout_pol_dlg;
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.PASSWORD_AND_USER_LOGOUT_POLICY, ActionType.CREATE);
        this.loadSettingsData();
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    onChange(type?: string) {
        this.isDirty = this.viewModel.isDirty();
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            let visiblePopovers = this.errorPopovers?.toArray()
                .filter(popover => popover.resScrollablePopover);
            if (visiblePopovers && visiblePopovers.length > 0) {
                visiblePopovers = visiblePopovers.sort((a, b) => {
                    const rectA = a.inputElement.getBoundingClientRect();
                    const rectB = b.inputElement.getBoundingClientRect();
                    if (rectA.top > rectB.top) { return 1; }
                    if (rectA.top < rectB.top) { return -1; }
                    if (rectA.top === rectB.top) { return 0; }
                });
                visiblePopovers[0].focusOnInput();
                resolve(false);
            } else {
                const requestInfo: any = {
                    enforceStrongPasswords: this.viewModel.model.enforceStrongPasswords,
                    minimumLowercaseLetters: this.viewModel.model.minimumLowercaseLetters,
                    minimumNumbers: this.viewModel.model.minimumNumbers,
                    minimumPasswordLength: this.viewModel.model.minimumPasswordLength,
                    minimumSymbols: this.viewModel.model.minimumSymbols,
                    minimumUppercaseLetters: this.viewModel.model.minimumUppercaseLetters,
                    passwordExpirationInDays: this.viewModel.model.passwordExpirationInDays,
                    systemScope: true
                };

                this.isBusyMessage = 'Saving Password and User Logout Policy ...';
                this.isBusy = true;

                if (!this.isTPAuthEnabled) {
                    this.restService.post(Endpoint.realmManager_setPasswordPolicy, requestInfo).then(response => {
                        this.resetAndLoad();
                        resolve(true);
                    }, error => {
                        this.isBusy = false;
                        resolve(false);
                    });
                }

                this.restService.post(Endpoint.realmManager_setInactivityTimeout, { inactivityTimeoutInSeconds: this.viewModel.model.inactivityTimeoutInSeconds * 60, systemScope: true }).then(response => {
                    this.resetAndLoad();
                    resolve(true);
                }, error => {
                    this.isBusy = false;
                    resolve(false);
                });
            }
        });
    }

    resetAndLoad(): void {
        this.isBusy = false;
        this.isDirty = false;
        this.clearErrors();
        this.loadSettingsData();
    }

    clearErrors(): void {
        this.errors = {
            passwordExpirationInDays: false,
            minimumLowercaseLetters: false,
            minimumNumbers: false,
            minimumPasswordLength: false,
            minimumSymbols: false,
            minimumUppercaseLetters: false,
            inactivityTimeoutInSeconds: false
        }
    }

    discardChanges(): void {
        this.isDirty = false;
        this.clearErrors();
        this.loadSettingsData();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (!isNullOrUndefined(this.viewModel.model.passwordExpirationInDays) ||
                (this.viewModel.model.enforceStrongPasswords && !isNullOrUndefined(this.viewModel.model.minimumLowercaseLetters) || !isNullOrUndefined(this.viewModel.model.minimumNumbers) || !isNullOrUndefined(this.viewModel.model.minimumPasswordLength) ||
                    !isNullOrUndefined(this.viewModel.model.minimumSymbols) || !isNullOrUndefined(this.viewModel.model.minimumUppercaseLetters)) || !isNullOrUndefined(this.viewModel.model.inactivityTimeoutInSeconds)) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    parseNumeric = (type: string): void => {
        let field = this.viewModel.model[type];
        if (isNullOrUndefined(field)) {
            if (type === 'passwordExpirationInDays') {
                field = this.defaultNinety;
            } else if (type === 'minimumPasswordLength') {
                field = this.defaultSix;
            } else if (type === 'inactivityTimeoutInSeconds') {
                field = this.defaultTwenty;
            } else {
                field = this.defaultZero;
            }

            this.errors[type] = true;
            this.viewModel.model[type] = field;
        }
    }

    validateInputOnPaste(type): void {
        setTimeout(() => {
            if ((this.viewModel?.model[type] === 0 && type !== 'passwordExpirationInDays' && type !== 'inactivityTimeoutInSeconds') || !this.viewModel?.model[type]?.toString().startsWith('0')) {
                return;
            }
            this.viewModel.model[type] = null;
        });
    }
}
